import React from 'react';
import { Box, Paper, Typography, Avatar, Button, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PeopleIcon from '@mui/icons-material/People';

const MAX_AVATARS = 5;

const getInitials = (name) =>
  String(name || '?').trim().split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2);

const getFirstName = (name) => String(name || 'Unknown').trim().split(' ')[0];

const CircleDetailPanel = ({ circle, onEditCircle, onDeleteCircle, onAddMember, onManageMembers }) => {
  const members = (circle.membersData || []).filter(m => m.id !== 0);

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Avatar sx={{ bgcolor: circle.color, width: 48, height: 48, fontWeight: 600 }}>
            {getInitials(circle.name)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
              {circle.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {circle.members} member{circle.members === 1 ? '' : 's'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => onAddMember(circle)}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Add member
          </Button>
          <Tooltip title="Manage members">
            <IconButton size="small" onClick={() => onManageMembers(circle)}>
              <PeopleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit circle name">
            <IconButton size="small" onClick={() => onEditCircle(circle)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete circle">
            <IconButton size="small" color="error" onClick={() => onDeleteCircle(circle.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {members.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No members yet. Add someone to get started.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
          <Box sx={{ display: 'flex' }}>
            {members.slice(0, MAX_AVATARS).map((member, i) => (
              <Avatar
                key={member.id}
                src={member.profile_picture || undefined}
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: 13,
                  ml: i === 0 ? 0 : -1.25,
                  border: '2px solid',
                  borderColor: 'background.paper',
                  zIndex: MAX_AVATARS - i,
                }}
              >
                {getInitials(member.name)}
              </Avatar>
            ))}
            {members.length > MAX_AVATARS && (
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: 12,
                  fontWeight: 600,
                  ml: -1.25,
                  border: '2px solid',
                  borderColor: 'background.paper',
                  bgcolor: 'grey.300',
                  color: 'text.secondary',
                  zIndex: 0,
                }}
              >
                +{members.length - MAX_AVATARS}
              </Avatar>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
            {members.slice(0, MAX_AVATARS).map(member => getFirstName(member.name)).join(', ')}
            {members.length > MAX_AVATARS && ` +${members.length - MAX_AVATARS}`}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default CircleDetailPanel;
